import { Injectable } from '@angular/core'
import { ApiService } from '../../services/api.service'
import { AuthRedirectService } from './auth-redirect.service'
import { AuthBroadcast, SessionStateService } from './session-state.service'

/**
 * Keeps every open tab in step with the shared server session. A logout (manual,
 * idle or expired session) or a login as a different user in one tab signs this
 * tab out and sends it to /login (after a different-user login that reload lands on
 * the new user's dashboard), so no tab keeps showing a user the cookie no longer
 * belongs to.
 */
@Injectable({ providedIn: 'root' })
export class AuthSyncService {
    private started = false

    private lastAnnouncedUserId: number | string | null = null

    constructor(
        private session: SessionStateService,
        private api: ApiService,
        private redirect: AuthRedirectService
    ) {}

    start(): void {
        if (this.started) {
            return
        }
        this.started = true
        this.lastAnnouncedUserId = this.currentUserId()

        // Every login path (form, social, verification links) ends by emitting true here
        this.api.userLoggedInSource.subscribe((loggedIn) => {
            if (!loggedIn) {
                this.lastAnnouncedUserId = null
                return
            }
            const id = this.api.user?.id ?? null
            if (id !== null && id !== this.lastAnnouncedUserId) {
                this.lastAnnouncedUserId = id
                this.session.announce({ type: 'login', userId: id })
            }
        })

        this.session.remote$.subscribe((msg) => this.onRemote(msg))
    }

    private currentUserId(): number | string | null {
        return this.api.user?.id ?? this.session.user?.id ?? null
    }

    private onRemote(msg: AuthBroadcast): void {
        const mine = this.currentUserId()
        if (mine === null) {
            return // this tab is not signed in: nothing to end
        }
        if (msg?.type === 'login' && msg.userId === mine) {
            return // same user signed in again elsewhere: this tab stays valid
        }
        if (msg?.type !== 'login' && msg?.type !== 'logout') {
            return
        }

        this.lastAnnouncedUserId = null
        this.session.logOut({ remote: true })
        this.api.logOut({ remote: true })

        if (msg.type === 'login') {
            // localStorage now holds the NEW user, so the login route's guard would bounce a
            // router navigation. A full load starts clean and lands on that user's dashboard.
            window.location.assign('/login')
            return
        }
        this.redirect.redirectToLogin('auth')
    }
}
